import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import useSWR from 'swr'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import {
  Alert, type AlertColor, Button, FormControl, InputLabel, MenuItem, Select, Snackbar, Stack, TextField,
  Typography, FormHelperText, Card, CardContent, Grid, useTheme, useMediaQuery,
} from '@mui/material'
import EastIcon from '@mui/icons-material/East'
import SouthIcon from '@mui/icons-material/South'
import { isEmptyString, isValidNumberString } from '../../utils/string-utils'
import {
  type ITransferAccount, type ITransferSubAccount, type ITransferSubmissionWithMain, type ITransferSubmissionWithoutMain,
} from '../../models/transfer/transfer-info-types-bybit'
import CenteredCardLayout from '../../layouts/centered-card-layout'
import Chip from '@mui/material/Chip'

enum TRANSFER_TYPE {
  MAIN_TO_SUB = 'Main Account to Sub-Account',
  SUB_TO_MAIN = 'Sub-Account to Main Account',
  SUB_TO_SUB = 'Sub-Account to Sub-Account'
}

enum ACCOUNT_TYPE {
  FUND = 'FUND',
  LINEAR = 'linear',
  UNKNOWN = '-'
}

const ACCOUNT_TYPE_NUMERIC_VALUES = new Map<ACCOUNT_TYPE, string>()
ACCOUNT_TYPE_NUMERIC_VALUES.set(ACCOUNT_TYPE.FUND, 'FUND')
ACCOUNT_TYPE_NUMERIC_VALUES.set(ACCOUNT_TYPE.LINEAR, 'LINEAR')
ACCOUNT_TYPE_NUMERIC_VALUES.set(ACCOUNT_TYPE.UNKNOWN, '-')

function createResponseForTransferInvolvingMain (
  transferType: TRANSFER_TYPE,
  mainAccount: string, targetAccount: string, sourceAccountType: ACCOUNT_TYPE, targetAccountType: ACCOUNT_TYPE,
  coin: string, amount: string): ITransferSubmissionWithMain {
  return {
    masterAccount: mainAccount,
    type: '1',
    internalTransferType: transferType === TRANSFER_TYPE.MAIN_TO_SUB ? '1' : '2',
    from: ACCOUNT_TYPE_NUMERIC_VALUES.get(sourceAccountType) ?? '',
    to: ACCOUNT_TYPE_NUMERIC_VALUES.get(targetAccountType) ?? '',
    subAcct: targetAccount,
    ccy: coin,
    amt: amount,
  }
}

function createResponseForTransferNotInvolvingMain (
  mainAccount: string,
  sourceAccount: string, targetAccount: string, sourceAccountType: ACCOUNT_TYPE, targetAccountType: ACCOUNT_TYPE,
  coin: string, amount: string): ITransferSubmissionWithoutMain {
  return {
    masterAccount: mainAccount,
    type: '2',
    from: ACCOUNT_TYPE_NUMERIC_VALUES.get(sourceAccountType) ?? '',
    to: ACCOUNT_TYPE_NUMERIC_VALUES.get(targetAccountType) ?? '',
    fromSubAccount: sourceAccount,
    toSubAccount: targetAccount,
    ccy: coin,
    amt: amount,
  }
}

const TransferViewBybit: React.FC = () => {
  const theme = useTheme()
  const isLargerThanMD = useMediaQuery(theme.breakpoints.up('md'))

  const { data, error } = useSWR<ITransferAccount[], boolean>(
    process.env.REACT_APP_API_TRANSFER_BYBIT_INDEX,
    basicFetcher,
    {
      shouldRetryOnError: false,
    },
  )

  const [mainAccountsMap, setMainAccountsMap] = useState<Map<string, ITransferAccount>>(new Map())
  const [mainToSubAccountsMap, setMainToSubAccountsMap] = useState<Map<string, Map<string, ITransferSubAccount>>>(new Map())

  useEffect(() => {
    let mainAccounts: ITransferAccount[]
    if (error === true || data === undefined) {
      mainAccounts = []
    } else {
      mainAccounts = data
    }

    const mainAccountsMap = new Map<string, ITransferAccount>()
    const subAccountsMap = new Map<string, Map<string, ITransferSubAccount>>()

    for (const mainAccount of mainAccounts) {
      if (isEmptyString(mainAccount.main_account)) {
        console.error('[Transfer] Empty main account')
        continue
      }

      const mainAccountName = String(mainAccount.main_account)
      if (mainAccountsMap.has(mainAccountName)) {
        console.error(`[Transfer] Duplicate main account: ${mainAccountName}`)
        continue
      }
      mainAccountsMap.set(mainAccountName, mainAccount)

      if (!subAccountsMap.has(mainAccountName)) {
        subAccountsMap.set(mainAccountName, new Map<string, ITransferSubAccount>())
      }

      const subAccountMap = subAccountsMap.get(mainAccountName)
      const subAccounts = mainAccount.sub_accounts
      for (const subAccount of subAccounts) {
        const { sub_account_name: subAccountName } = subAccount
        subAccountMap?.set(subAccountName, subAccount)
      }
    }

    setMainAccountsMap(mainAccountsMap)
    setMainToSubAccountsMap(subAccountsMap)
  }, [data, error])

  // Main
  const [mainAccount, setMainAccount] = useState<string>('')
  const [transferType, setTransferType] = useState<TRANSFER_TYPE>(TRANSFER_TYPE.MAIN_TO_SUB)
  // Source
  const [sourceAccount, setSourceAccount] = useState<string>('')
  const [sourceAccountType, setSourceAccountType] = useState<ACCOUNT_TYPE>(ACCOUNT_TYPE.UNKNOWN)
  // Target
  const [targetAccount, setTargetAccount] = useState<string>('')
  const [targetAccountType, setTargetAccountType] = useState<ACCOUNT_TYPE>(ACCOUNT_TYPE.UNKNOWN)
  // Transfer Options
  const [transferCoin, setTransferCoin] = useState<string>('')
  const [transferAmount, setTransferAmount] = useState<string>('')
  // Target heplper text
  const [targetAccountHelperText, setTargetAccountHelperText] = useState<string>('select..')
  const [transferWarning, setTransferWarning] = useState<boolean>(false)

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')

  // submit confirm message
  const [showConfirmMsg, setShowConfirmMsg] = useState(false)

  const displayToastWithMessage = (message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }

  useEffect(() => {
    // sourceAccount, sourceAccountType, targetAccount and targetAccountType have been reset prior to this
    switch (transferType) {
      case TRANSFER_TYPE.MAIN_TO_SUB:
        setSourceAccount(mainAccount)
        break
      case TRANSFER_TYPE.SUB_TO_MAIN:
        setTargetAccount(mainAccount)
        break
      case TRANSFER_TYPE.SUB_TO_SUB:
        // No operation
        break
    }
  }, [transferType, mainAccount])

  useEffect(() => {
    /** set a warning message if we are transferring between accounts of different family (for example, okx_bcp1, exchange=okx, family=bcp, id=1 */
    const regexp = /(?<exchange>...)_(?<family>\D+)(?<id>\d*$)/

    const targetAccountMatch = targetAccount.match(regexp)
    const sourceAccountMatch = sourceAccount.match(regexp)

    if (isEmptyString(sourceAccount) || isEmptyString(targetAccount)) {
      setTargetAccountHelperText('select..')
      setTransferWarning(false)
    } else if (((sourceAccountMatch?.groups?.exchange) != null) && ((sourceAccountMatch?.groups?.family) != null) &&
        ((targetAccountMatch?.groups?.exchange) != null) && ((targetAccountMatch?.groups?.family) != null)) {
      if (sourceAccountMatch.groups.exchange === targetAccountMatch.groups.exchange && sourceAccountMatch.groups.family === targetAccountMatch.groups.family) {
        setTargetAccountHelperText('same family transfer')
        setTransferWarning(false)
      } else {
        setTargetAccountHelperText('warning: cross-family transfer')
        setTransferWarning(true)
      }
    } else {
      setTargetAccountHelperText('same family transfer')
      setTransferWarning(false)
    }
  }, [sourceAccount, targetAccount])

  const onSubmitConfirmMsg = (event: React.FormEvent): void => {
    event.preventDefault()
    setShowConfirmMsg(!showConfirmMsg)
  }

  const onSubmit = (event: React.FormEvent): void => {
    if (!showConfirmMsg) {
      /** ask user to confirm the on first time clicking the submit button */
      event.preventDefault()
      setShowConfirmMsg(true)
      return
    }

    event.preventDefault()

    if (isEmptyString(process.env.REACT_APP_API_TRANSFER_BYBIT_EXECUTE) || process.env.REACT_APP_API_TRANSFER_BYBIT_EXECUTE == null) {
      console.error('[Transfer] Failed to execute transfer (reason: invalid execute url)')
      return
    }

    let body

    switch (transferType) {
      case TRANSFER_TYPE.MAIN_TO_SUB: {
        const mainToSubTargetAccount = mainToSubAccountsMap.get(mainAccount)?.get(targetAccount)

        if (mainToSubTargetAccount == null) {
          console.error('[Transfer] Failed to execute transfer (reason: sub-account does not exist)')
          return
        }

        body = createResponseForTransferInvolvingMain(
          transferType, mainAccount, mainToSubTargetAccount.sub_account_init_name, sourceAccountType, targetAccountType, transferCoin, transferAmount,
        )
      }
        break
      case TRANSFER_TYPE.SUB_TO_MAIN: {
        const subToMainSourceAccount = mainToSubAccountsMap.get(mainAccount)?.get(sourceAccount)

        if (subToMainSourceAccount == null) {
          console.error('[Transfer] Failed to execute transfer (reason: sub-account does not exist)')
          return
        }

        body = createResponseForTransferInvolvingMain(
          transferType, mainAccount, subToMainSourceAccount.sub_account_init_name, sourceAccountType, targetAccountType, transferCoin, transferAmount,
        )
      }
        break
      case TRANSFER_TYPE.SUB_TO_SUB: {
        const sourceSubAccount = mainToSubAccountsMap.get(mainAccount)?.get(sourceAccount)
        const targetSubAccount = mainToSubAccountsMap.get(mainAccount)?.get(targetAccount)

        if (sourceSubAccount == null) {
          console.error('[Transfer] Failed to execute transfer (reason: source sub-account does not exist)')
          return
        }
        if (targetSubAccount == null) {
          console.error('[Transfer] Failed to execute transfer (reason: target sub-account does not exist)')
          return
        }

        body = createResponseForTransferNotInvolvingMain(
          mainAccount, sourceSubAccount.sub_account_init_name, targetSubAccount.sub_account_init_name, sourceAccountType, targetAccountType, transferCoin, transferAmount,
        )
      }
        break
    }

    console.info('[Transfer] Submitting bybit transfer:')
    console.info(body)

    axios
      .post(process.env.REACT_APP_API_TRANSFER_BYBIT_EXECUTE, body)
      .then(response => {
        console.info(response)

        if (Object.hasOwn(response, 'data')) {
          const { code, data, msg }: { code: string, data: string, msg: string } = response?.data
          const toastMessage = `Transfer Succeeded
            Code: ${code}
            Data: ${data}
            Msg: ${msg}
            `
          displayToastWithMessage(toastMessage, 'success')
        }
      })
      .catch(reason => {
        console.error(reason)

        let toastMessage
        if (Object.hasOwn(reason, 'data')) {
          const { code, data, msg }: { code: string, data: string, msg: string } = reason?.data
          toastMessage = `Transfer Failed
          Code: ${code}
          Data: ${data}
          Msg: ${msg}
          ( Full error can also be viewed in the console logs )`
        } else {
          toastMessage = `Transfer Failed
          Error: ${reason.message as string}
          ( Full error can also be viewed in the console logs )`
        }

        displayToastWithMessage(toastMessage, 'error')
      })

    setShowConfirmMsg(false)
  }

  const coinList = useMemo(() => {
    if (isEmptyString(sourceAccount)) {
      return []
    }

    if (transferType === TRANSFER_TYPE.MAIN_TO_SUB) {
      return mainAccountsMap.get(sourceAccount)?.coin_list
        .sort((a, b) => a.coin.localeCompare(b.coin, undefined, { numeric: true }))
        .filter(a => a.account_type === sourceAccountType)
        .map(coin => {
          return <MenuItem key={coin.coin} value={coin.coin}>{coin.coin}</MenuItem>
        }) ?? []
    } else {
      return mainToSubAccountsMap.get(mainAccount)?.get(sourceAccount)?.coin_list
        .sort((a, b) => a.coin.localeCompare(b.coin, undefined, { numeric: true }))
        .filter(a => a.account_type === sourceAccountType)
        .map(coin => {
          return <MenuItem key={coin.coin} value={coin.coin}>{coin.coin}</MenuItem>
        }) ?? []
    }
  }, [mainAccountsMap, mainToSubAccountsMap, mainAccount, sourceAccount, transferType, sourceAccountType])

  const coinBalance = useMemo(() => {
    if (isEmptyString(sourceAccount)) {
      return '0'
    }

    if (transferType === TRANSFER_TYPE.MAIN_TO_SUB) {
      const coinData = mainAccountsMap.get(sourceAccount)?.coin_list
        .filter(a => a.account_type === sourceAccountType)
        .find(a => a.coin === transferCoin)
      return coinData != null ? coinData.amount : '0'
    } else {
      const coinData = mainToSubAccountsMap.get(mainAccount)?.get(sourceAccount)?.coin_list
        .filter(a => a.account_type === sourceAccountType)
        .find(a => a.coin === transferCoin)
      return coinData != null ? coinData.amount : '0'
    }
  }, [mainAccountsMap, mainToSubAccountsMap, mainAccount, sourceAccount, transferType, sourceAccountType, transferCoin])

  return (
    <CenteredCardLayout>
      <form id='transfer-form' onSubmit={onSubmitConfirmMsg}>
        <Typography gutterBottom variant="subtitle1">Bybit Transfer</Typography>
        <Stack gap={1} alignItems='center'>
          <Card variant='outlined' sx={{ width: 1, padding: 2 }}>
            <CardContent>
              <Stack gap={2}>
                <FormControl variant='standard'>
                  <InputLabel id='control-account-select-label'>Main Account</InputLabel>
                  <Select
                    labelId='control-account-select-label'
                    id='control-account-select'
                    label='Main Account'
                    value={mainAccount}
                    onChange={event => {
                      setSourceAccount('')
                      setTargetAccount('')
                      setTransferCoin('')
                      setTransferAmount('')
                      setSourceAccountType(ACCOUNT_TYPE.UNKNOWN)
                      setTargetAccountType(ACCOUNT_TYPE.UNKNOWN)
                      setMainAccount(event.target.value)
                    }}
                  >
                    {
                      Array.from(mainToSubAccountsMap.keys())
                        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                        .map(key => {
                          return <MenuItem key={key} value={key}>{key}</MenuItem>
                        })
                    }
                  </Select>
                </FormControl>

                <FormControl variant='standard' disabled={isEmptyString(mainAccount)}>
                  <InputLabel id='transfer-type-select-label'>Transfer Type</InputLabel>
                  <Select
                    labelId='transfer-type-type-select-label'
                    id='transfer-type-type-select'
                    label='Transfer Type'
                    value={transferType}
                    onChange={event => {
                      const selectedTransferType = event.target.value as TRANSFER_TYPE
                      setSourceAccount('')
                      setTargetAccount('')
                      setTransferCoin('')
                      setTransferAmount('')
                      setSourceAccountType(ACCOUNT_TYPE.UNKNOWN)
                      setTargetAccountType(ACCOUNT_TYPE.UNKNOWN)
                      setTransferType(selectedTransferType)
                    }}
                  >
                    <MenuItem value={TRANSFER_TYPE.MAIN_TO_SUB}>{TRANSFER_TYPE.MAIN_TO_SUB}</MenuItem>
                    <MenuItem value={TRANSFER_TYPE.SUB_TO_MAIN}>{TRANSFER_TYPE.SUB_TO_MAIN}</MenuItem>
                    <MenuItem value={TRANSFER_TYPE.SUB_TO_SUB}>{TRANSFER_TYPE.SUB_TO_SUB}</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>

          <Card variant='outlined' sx={{ width: 1, padding: 2 }}>
            <CardContent>
              <Grid container alignItems='center' spacing={4}>
                <Grid
                  item xs
                  display='flex'
                  flexDirection='column'
                  gap={2}
                >
                  <FormControl
                    variant='standard'
                    sx={{ minWidth: 300 }}
                    disabled={isEmptyString(mainAccount) || transferType === TRANSFER_TYPE.MAIN_TO_SUB}
                  >
                    <InputLabel id='source-account-select-label'>Source Account</InputLabel>
                    <Select
                      labelId='source-account-select-label'
                      id='source-account-select'
                      label='Source Account'
                      value={sourceAccount}
                      onChange={event => {
                        setTransferCoin('')
                        setTransferAmount('')
                        setSourceAccountType(ACCOUNT_TYPE.UNKNOWN)
                        setSourceAccount(event.target.value)
                      }}
                    >
                      {
                        transferType === TRANSFER_TYPE.MAIN_TO_SUB &&
                        <MenuItem key={mainAccount} value={mainAccount}>{mainAccount}</MenuItem>
                      }
                      {
                        Array.from(mainToSubAccountsMap.get(mainAccount)?.keys() ?? [])
                          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                          .map(key => {
                            return <MenuItem key={key} value={key}>{key}</MenuItem>
                          })
                      }
                    </Select>
                    <FormHelperText id="source-account-helper-text"> </FormHelperText>
                  </FormControl>

                  <FormControl variant='standard' disabled={isEmptyString(mainAccount)}>
                    <InputLabel id='source-account-type-select-label'>Source Type</InputLabel>
                    <Select
                        labelId='source-account-type-select-label'
                        id='source-account-type-select'
                        label='Source Type'
                        value={sourceAccountType}
                        onChange={event => {
                          const selectedAccountType = event.target.value as ACCOUNT_TYPE
                          setSourceAccountType(selectedAccountType)
                          setTransferAmount('')
                        }}
                    >
                      <MenuItem value={ACCOUNT_TYPE.FUND}>{ACCOUNT_TYPE.FUND}</MenuItem>
                      <MenuItem value={ACCOUNT_TYPE.LINEAR}>{ACCOUNT_TYPE.LINEAR}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  item xs={12} md='auto'
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                  minWidth='60px'
                  gap={2}
                >
                  {
                    isLargerThanMD ? <EastIcon fontSize='large'/> : <SouthIcon fontSize='large'/>
                  }
                </Grid>

                <Grid
                  item xs
                  display='flex'
                  flexDirection='column'
                  gap={2}
                >
                  <FormControl
                    variant='standard'
                    sx={{ minWidth: 300 }}
                    disabled={isEmptyString(mainAccount) || transferType === TRANSFER_TYPE.SUB_TO_MAIN}
                  >
                    <InputLabel id='target-account-select-label'>Target Account</InputLabel>
                    <Select
                      labelId='target-account-select-label'
                      id='target-account-select'
                      name='account'
                      label='Target Account'
                      value={targetAccount}
                      onChange={event => {
                        setTargetAccount(event.target.value)
                        setTransferAmount('')
                        setTargetAccountType(ACCOUNT_TYPE.UNKNOWN)
                      }}
                    >
                      {
                        transferType === TRANSFER_TYPE.SUB_TO_MAIN &&
                        <MenuItem key={mainAccount} value={mainAccount}>{mainAccount}</MenuItem>
                      }
                      {
                        Array.from(mainToSubAccountsMap.get(mainAccount)?.keys() ?? [])
                          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                          .map(key => {
                            return <MenuItem key={key} value={key}>{key}</MenuItem>
                          })
                      }
                    </Select>
                    <FormHelperText id="target-account-helper-text" error={transferWarning}>{targetAccountHelperText}</FormHelperText>
                  </FormControl>
                  <FormControl variant='standard' disabled={isEmptyString(mainAccount)}>
                    <InputLabel id='target-account-type-select-label'>Target Type</InputLabel>
                    <Select
                        labelId='target-account-type-select-label'
                        id='target-account-type-select'
                        label='Target Type'
                        value={targetAccountType}
                        onChange={event => {
                          const selectedAccountType = event.target.value as ACCOUNT_TYPE
                          setTargetAccountType(selectedAccountType)
                        }}
                    >
                      <MenuItem value={ACCOUNT_TYPE.FUND}>{ACCOUNT_TYPE.FUND}</MenuItem>
                      <MenuItem value={ACCOUNT_TYPE.LINEAR}>{ACCOUNT_TYPE.LINEAR}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant='outlined' sx={{ width: 1, padding: 2 }}>
            <CardContent>
              <Stack gap={2}>
                <FormControl
                  variant='standard'
                  disabled={isEmptyString(sourceAccount)}
                  error={coinList.length === 0 && !isEmptyString(sourceAccount)}
                >
                  <InputLabel id='transfer-coin-select-label'>Coin</InputLabel>
                  <Select
                    labelId='transfer-coin-select-label'
                    id='transfer-coin-select'
                    name='coin'
                    label='Coin'
                    value={transferCoin}
                    onChange={event => {
                      setTransferCoin(event.target.value)
                      setTransferAmount('')
                    }}
                  >
                    {coinList ?? null}
                  </Select>
                  <FormHelperText>{
                    (coinList.length === 0 && !isEmptyString(sourceAccount))
                      ? `There are no valid coins to transfer out from '${sourceAccount}'.`
                      : null
                  }</FormHelperText>
                </FormControl>

                <Stack direction="row" spacing={1}>
                  <Chip label={coinBalance} variant="outlined"
                        onClick={event => {
                          setTransferAmount(coinBalance)
                        }}
                        onDelete={event => {
                          setTransferAmount('')
                        }}
                  />
                </Stack>

                <TextField
                  id='amount-text-field'
                  label='Amount'
                  type='number'
                  variant='standard'
                  sx={{ minHeight: '4.5rem' }}
                  disabled={isEmptyString(transferCoin)}
                  error={
                    !isEmptyString(transferCoin) && (!(isValidNumberString(transferAmount) &&
                      Number(transferAmount) > 0))
                  }
                  helperText={
                    !isEmptyString(transferCoin) && (!(isValidNumberString(transferAmount) && Number(transferAmount) > 0)) &&
                    'Amount must be a positive number greater than zero'
                  }
                  value={transferAmount}
                  onChange={event => {
                    setTransferAmount(event.target.value)
                  }}
                />

                <Button
                  variant='contained'
                  type='submit'
                  disabled={
                    isEmptyString(mainAccount) ||
                    (isEmptyString(sourceAccount) || isEmptyString(targetAccount)) ||
                    (sourceAccountType == null || sourceAccountType === ACCOUNT_TYPE.UNKNOWN) ||
                    (targetAccountType == null || targetAccountType === ACCOUNT_TYPE.UNKNOWN) ||
                    isEmptyString(transferCoin) ||
                    (!(isValidNumberString(transferAmount) && Number(transferAmount) > 0))
                  }
                >
                  Submit
                </Button>

                {showConfirmMsg && (
                    <div>
                      <Stack spacing={2} direction="row">
                        <Alert severity="warning">Are you sure to submit this transfer?</Alert>
                        <Button variant='outlined' color="success" type="submit" onClick={onSubmit}>Yes</Button>
                        <Button variant='outlined' color="error" type="submit" onClick={event => { setShowConfirmMsg(false) }}>No</Button>
                      </Stack>
                    </div>
                )}

                <Stack>
                  <Typography
                    variant='caption'
                    color='error'
                    sx={{
                      display: (!isEmptyString(sourceAccount) &&
                        !isEmptyString(targetAccount) && sourceAccount === targetAccount)
                        ? 'inline'
                        : 'none',
                    }}
                  >
                    Source account and target account cannot be the same account
                  </Typography>
                </Stack>

                <Snackbar
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  autoHideDuration={60000}
                  open={toastOpen}
                  onClose={() => {
                    setToastOpen(false)
                  }}
                >
                  <Alert
                    sx={{ width: 1, fontSize: 'medium', whiteSpace: 'pre-line' }}
                    variant='filled'
                    onClose={() => {
                      setToastOpen(false)
                    }}
                    severity={toastSeverity}
                  >
                    {toastMessage}
                  </Alert>
                </Snackbar>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </form>
    </CenteredCardLayout>
  )
}

export default TransferViewBybit
